import { theme } from '@Guidelines/Theme/theme';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import formatEuros from '@UI/Helpers/formatEuros';
import type AiCostLimitView from '@UI/Store/AiCostLimits/Views/AiCostLimitView';
import { css } from '@emotion/react';
import * as React from 'react';
import { ProgressBar } from '@verclam/ui-kit';

const gaugeStyle = css`
	position: relative;
`;

const thresholdStyle = css`
	position: absolute;
	top: -3px;
	bottom: -3px;
	width: 2px;
	background-color: ${theme.colors.ink};
	opacity: 0.35;
`;

const lineStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.semibold};
`;

const noteStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

const barStyle = { height: '10px' };

type Props = {
	readonly limit: AiCostLimitView;
	readonly monthName: string;
	/** Share of the limit expected at the end of the month; `null` when the period does not end today. */
	readonly projectedPercent: number | null;
	/** Cost of the filtered scope and its name, when the limit is wider than the filter. */
	readonly part: { readonly perimeter: string; readonly costAmount: number } | null;
};

const resolveTone = (limit: AiCostLimitView, percent: number): { bar: string; text: string } => {
	if (percent >= 100) {
		return { bar: theme.states.danger.solid, text: theme.states.danger.solid };
	}

	if (percent >= limit.alertThresholdPercent) {
		return { bar: theme.states.warning.solid, text: theme.states.warning.solid };
	}

	return { bar: theme.colors.validated, text: theme.states.success.ink };
};

/** Consumption of the monthly limit: gauge with the alert-threshold mark, the figures and the projection. */
const LimitGauge: React.FC<Props> = ({ limit, monthName, projectedPercent, part }) => {
	const tone = resolveTone(limit, limit.percent);
	const thresholdStyleWithPosition = React.useMemo(
		() => [thresholdStyle, css({ left: `${Math.min(100, limit.alertThresholdPercent)}%` })],
		[limit.alertThresholdPercent],
	);

	const consumedText = React.useMemo(() => {
		const base = `${limit.percent} % du plafond de ${formatEuros(limit.monthlyAmount)} consommé en ${monthName}`;

		if (limit.percent >= 100) {
			return `${base} : plafond atteint.`;
		}

		if (limit.percent >= limit.alertThresholdPercent) {
			return `${base} : seuil d'alerte de ${limit.alertThresholdPercent} % dépassé.`;
		}

		return `${base}.`;
	}, [limit, monthName]);

	const projectionText = React.useMemo(() => {
		if (projectedPercent === null) {
			return null;
		}

		const base = `À ce rythme : environ ${Math.round(projectedPercent)} % du plafond fin ${monthName}.`;

		return projectedPercent >= 100 ? `${base} Le plafond serait dépassé avant la fin du mois.` : base;
	}, [projectedPercent, monthName]);

	return (
		<React.Fragment>
			<div css={gaugeStyle}>
				<ProgressBar
					value={Math.min(100, limit.percent)}
					showValue={false}
					color={tone.bar}
					style={barStyle}
					aria-label={`${limit.percent} % du plafond consommé en ${monthName}`}
				/>
				<span css={thresholdStyleWithPosition} aria-hidden="true" />
			</div>
			<p css={lineStyle} style={{ color: tone.text }}>
				{consumedText}
			</p>
			<ConditionalLoader data={projectionText}>
				{(text) => <p css={noteStyle}>{text}</p>}
			</ConditionalLoader>
			<ConditionalLoader data={part}>
				{(scope) => (
					<p css={noteStyle}>
						{`${scope.perimeter} : ${formatEuros(scope.costAmount)} ce mois-ci, soit ${Math.round((scope.costAmount / limit.monthlyAmount) * 100)} % de ce plafond.`}
					</p>
				)}
			</ConditionalLoader>
		</React.Fragment>
	);
};

export default LimitGauge;
